import React, { PropsWithChildren, createContext, useState } from 'react';

export interface IPlan {
  id: string;
  desc: string;
  active: boolean;
  miles: string;
}

export interface IMember {
  memberNumVoeBiz: string;
  name: string;
  cpf: string;
  email: string;
  birthdate: string;
  status: string;
  created: string;
  currentPlan: {
    id: string;
    desc: string;
  };
  planList: IPlan[];
}

interface ISelectedMembersContext {
  selectedMembers: IMember[];
  addMember: (member: IMember | IMember[]) => void;
  removeMember: (member: IMember | IMember[]) => void;
  clearMembers: () => void;
  containsMember: (member: IMember) => boolean;
}

const SelectedMembersContext = createContext<ISelectedMembersContext>({} as ISelectedMembersContext);

function SelectedMembersProvider({ children }: PropsWithChildren): JSX.Element {
  const [selectedMembers, setSelectedMembers] = useState<IMember[]>([]);

  const addMember = (member: IMember | IMember[]) => {
    if (Array.isArray(member)) {
      setSelectedMembers(() => [...selectedMembers, ...member]);
    } else {
      setSelectedMembers(() => [...selectedMembers, member]);
    }
  };

  const removeMember = (member: IMember | IMember[]) => {
    if (Array.isArray(member)) {
      const members: IMember[] = [];

      selectedMembers.forEach((selectedMember) => {
        if (!member.some((m) => m.cpf === selectedMember.cpf)) {
          members.push(selectedMember);
        }
      });

      setSelectedMembers(() => members);
    } else {
      const members = selectedMembers.filter((selectedMember) => selectedMember.cpf !== member.cpf);
      setSelectedMembers(() => members);
    }
  };

  const clearMembers = () => {
    setSelectedMembers([]);
  };

  const containsMember = (member: IMember) => {
    return selectedMembers.some((selectedMember) => selectedMember.cpf === member.cpf);
  };

  return (
    <SelectedMembersContext.Provider value={{ selectedMembers, addMember, removeMember, clearMembers, containsMember }}>
      {children}
    </SelectedMembersContext.Provider>
  );
}

function useSelectedMembers(): ISelectedMembersContext {
  const context = React.useContext(SelectedMembersContext);
  return context;
}

export { useSelectedMembers };

export default SelectedMembersProvider;
